const fileTypes = Object.freeze({
  STUDENT_ROSTER: 'STUDENT_ROSTER',
  COURSE_CATALOG: 'COURSE_CATALOG',
  STUDENT_DEMOGRAPHICS: 'STUDENT_DEMOGRAPHICS',
  STUDENT_TRANSCRIPTS: 'TRANSCRIPT',
  TEST_SCORES: 'STUDENT_TEST_SCORES',
  CTE_PATHWAYS: 'CTE_PATHWAY_CATALOG',
  FOCUS_GROUP_TRANSCRIPTION: 'FOCUS_GROUP_TRANSCRIPTION',
  FOCUS_GROUP_RECORDING: 'FOCUS_GROUP_RECORDING',
  FOCUS_GROUP_FINALIZED_REPORT: 'FOCUS_GROUP_FINALIZED_REPORT',
  SURVEY_REPORT_FINALIZED: 'SURVEY_REPORT_FINALIZED',
  SJW_ARTIFACT_PHOTO: 'SJW_ARTIFACT_PHOTO',
});

const fileTypesLabels = Object.freeze({
  [fileTypes.STUDENT_ROSTER]: 'Student Roster',
  [fileTypes.COURSE_CATALOG]: 'Course Catalog',
  [fileTypes.STUDENT_DEMOGRAPHICS]: 'Student Demographics',
  [fileTypes.STUDENT_TRANSCRIPTS]: 'Student Transcripts',
  [fileTypes.TEST_SCORES]: 'Test Scores',
  [fileTypes.CTE_PATHWAYS]: 'CTE Pathways',
  [fileTypes.FOCUS_GROUP_TRANSCRIPTION]: 'Focus Group Transcripts',
  [fileTypes.FOCUS_GROUP_RECORDING]: 'Focus Group Recordings',
  [fileTypes.FOCUS_GROUP_FINALIZED_REPORT]: 'Focus Group Finalized Report',
  [fileTypes.SURVEY_REPORT_FINALIZED]: 'Survey Report Finalized',
  [fileTypes.SJW_ARTIFACT_PHOTO]: 'Student Journey Workshop Photo',
});

const DATA_CATEGORY_LABELS = Object.freeze({
  [fileTypes.STUDENT_ROSTER]: 'Student Roster',
  [fileTypes.COURSE_CATALOG]: 'Course Catalog',
  [fileTypes.STUDENT_DEMOGRAPHICS]: 'Student Demographics',
  [fileTypes.STUDENT_TRANSCRIPTS]: 'Student Transcripts',
  [fileTypes.TEST_SCORES]: 'Test Scores',
  [fileTypes.CTE_PATHWAYS]: 'CTE Pathways',
});

const TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY = 'transcriptSelectionSchool';
const DATA_QC_SCHOOL_LOCAL_STORAGE_KEY = 'dataQcSchool';

const DISTRICT_LOCAL_STORAGE_KEY = 'district';
const SELECTED_EOA_LOCAL_STORAGE_KEY = 'selectedEoaId';
const SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY = 'selectedEoaPreference';
const SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY = 'transcript';
const EOA_PREFERENCE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const AUDIT_LOG_ITEMS_PER_PAGE = 10;
const DATA_UPLOAD_ITEMS_PER_PAGE = 10;
const DATA_QC_TRANSCRIPTS_ITEMS_PER_PAGE = 10;
const STUDENT_ROSTER_ITEMS_PER_PAGE = 10;
const USERS_PER_PAGE = 20;
const ACTIVITY_LOG_ITEMS_PER_PAGE = 20;
const CHAT_CONVERSATIONS_PER_PAGE = 5;

const USER_ROLES = Object.freeze({
  SUPER_ADMIN: 'SUPER_ADMIN',
  XQ_ADMIN: 'XQ_ADMIN',
  FACILITATOR: 'FACILITATOR',
  LEAD_FACILITATOR: 'LEAD_FACILITATOR',
  DISTRICT_DATA_LEAD: 'DISTRICT_DATA_LEAD',
  XQ_USER: 'XQ_USER',
  XQ_ACCOUNT_MANAGER: 'XQ_ACCOUNT_MANAGER',
  XQ_DATA_LEAD: 'XQ_DATA_LEAD',
  DISTRICT_SUPERINTENDENT: 'DISTRICT_SUPERINTENDENT',
});

const USER_ROLES_LABELS: Record<string, string> = Object.freeze({
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.XQ_ADMIN]: 'XQ Admin',
  [USER_ROLES.FACILITATOR]: 'Facilitator',
  [USER_ROLES.LEAD_FACILITATOR]: 'Lead Facilitator',
  [USER_ROLES.DISTRICT_DATA_LEAD]: 'District Data Lead',
  [USER_ROLES.XQ_USER]: 'XQ User',
  [USER_ROLES.XQ_ACCOUNT_MANAGER]: 'XQ Account Manager',
  [USER_ROLES.XQ_DATA_LEAD]: 'XQ Data Lead',
  [USER_ROLES.DISTRICT_SUPERINTENDENT]: 'District Superintendent',
});

const UPLOAD_SECTIONS_IDS = Object.freeze({
  [fileTypes.STUDENT_ROSTER]: 'studentRoster',
  [fileTypes.COURSE_CATALOG]: 'courseCatalog',
  [fileTypes.STUDENT_DEMOGRAPHICS]: 'studentDemographics',
  [fileTypes.STUDENT_TRANSCRIPTS]: 'studentTranscripts',
  [fileTypes.TEST_SCORES]: 'testScores',
  [fileTypes.CTE_PATHWAYS]: 'ctePathways',
  [fileTypes.FOCUS_GROUP_TRANSCRIPTION]: 'focusGroupTranscription',
});

const EOA_INVITE_ROLES = Object.freeze([
  USER_ROLES.FACILITATOR,
  USER_ROLES.LEAD_FACILITATOR,
  USER_ROLES.DISTRICT_DATA_LEAD,
]);

const PLATFORM_INVITE_ROLES = Object.freeze([USER_ROLES.XQ_ADMIN, USER_ROLES.SUPER_ADMIN]);

const NAVIGATION_LINKS_DATA = Object.freeze({
  SELECT_DISTRICT: {
    path: '/select-district',
    label: 'Select District',
  },
  OVERVIEW: {
    path: '/overview',
    label: 'Overview',
  },
  DASHBOARD: {
    path: '/dashboard',
    label: 'Dashboard',
  },
  DATA_UPLOAD: {
    path: '/data-upload',
    label: 'Data Upload',
  },
  DATA_QC: {
    path: '/data-qc',
    label: 'Quality Check',
  },
  COURSE_MAPPING: {
    path: '/course-mapping',
    label: 'Course mapping',
  },
  TRANSCRIPTS: {
    path: '/transcript-selection',
    label: 'Transcript Selection',
  },
  FOCUS_GROUPS: {
    path: '/school-focus-groups',
    label: 'Focus group tool',
  },
  FOCUS_GROUP: {
    path: '/school-focus-group/:schoolId/:focusGroupName?',
    label: 'Focus Group',
  },
  SURVEY_REPORTS: {
    path: '/survey-reports',
    label: 'Surveys',
  },
  SCHOOL_REPORTS: {
    path: '/school-reports',
    label: 'School Report',
  },
  CHAT: {
    path: '/district-report-tool',
    label: 'District Report Tool',
  },
  SURVEY_MANAGEMENT: {
    path: '/surveys',
    label: 'Survey Management',
  },
  SCHOOLS_CONFIG: {
    path: '/schools-config',
    label: 'Schools Configuration',
  },
  CHAT_CONFIG: {
    path: '/chat-config',
    label: 'Chat Configuration',
  },
  CONFIGURATION: {
    path: '/configuration',
    label: 'Configuration',
  },
  SURVEY_RESPONSE_DASHBOARD: {
    path: '/survey-response-dashboard',
    label: 'Surveys',
  },
  SJW_PHOTO_UPLOAD: {
    path: '/sjw-photo-upload',
    label: 'Workshop photo upload',
  },
  USER_MANAGEMENT: {
    path: '/user-management',
    label: 'User Management',
  },
  ACTIVITY_LOG: {
    path: '/activity-log',
    label: 'Activity Log',
  },
});

const DEMOGRAPHIC_PERCENTAGES = Object.freeze({
  RACE: {
    PACIFIC_ISLANDER: 'PACIFIC ISLANDER',
    WHITE: 'WHITE',
    ASIAN: 'ASIAN',
    UNKNOWN: 'UNKNOWN',
    AMERICAN_INDIAN: 'AMERICAN INDIAN',
    LATINX: 'LATINX',
    OTHER: 'OTHER',
    TWO_OR_MORE: 'TWO OR MORE',
    BLACK: 'BLACK',
  },
  INCOME: {
    LOW_INCOME: 'LOW-INCOME',
    ABOVE_LOW_INCOME: 'ABOVE LOW-INCOME',
  },
  ENGLISH_LEARNER: {
    NATIVE: 'NATIVE / FLUENT',
    LEARNER: 'ENGLISH LANGUAGE LEARNER',
    OTHER: 'OTHER',
  },
  DISABILITIES: {
    WITHOUT_DISABILITIES: 'WITHOUT DISABILITIES',
    WITH_DISABILITIES: 'WITH DISABILITIES',
  },
});

const TRANSCRIPTS_CATEGORIES = Object.freeze({
  RIGOROUS_ACADEMIC: 'RIGOROUS_ACADEMIC',
  ON_THE_CUSP: 'ON_THE_CUSP',
  STRUGGLING: 'STRUGGLING',
});

const TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS = Object.freeze({
  [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 1,
  [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 6,
  [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 1,
});

const TRANSCRIPTS_CATEGORIES_LABELS = Object.freeze({
  [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 'High fliers',
  [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 'On the cusp',
  [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 'Struggling',
});

const TRANSCRIPTS_PAGE_SIZES = Object.freeze({
  [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 4,
  [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 12,
  [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 4,
});

const ACADEMIC_PERIODS_NAMES = Object.freeze({
  NINTH_GRADE: 'NINTH_GRADE',
  TENTH_GRADE: 'TENTH_GRADE',
  ELEVENTH_GRADE: 'ELEVENTH_GRADE',
  TWELFTH_GRADE: 'TWELFTH_GRADE',
});

const ACADEMIC_PERIODS_LABELS = Object.freeze({
  [ACADEMIC_PERIODS_NAMES.NINTH_GRADE]: '9th grade',
  [ACADEMIC_PERIODS_NAMES.TENTH_GRADE]: '10th grade',
  [ACADEMIC_PERIODS_NAMES.ELEVENTH_GRADE]: '11th grade',
  [ACADEMIC_PERIODS_NAMES.TWELFTH_GRADE]: '12th grade',
});

const XQ_SUPPORT_EMAIL = 'support-eoaa@xqinstitute.org';

const XQ_INFO_EMAIL = 'info@xq.org';

const DEMOGRAPHIC_SETTINGS_DATA_KEYS = Object.freeze({
  GENDER: 'GENDER',
  RACE: 'RACE',
  LI: 'SOCIO_ECONOMIC',
  ELL: 'LANGUAGE_LEARNER',
  SWD: 'DISABILITY_STATUS',
});

export {
  fileTypes,
  TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY,
  DATA_QC_SCHOOL_LOCAL_STORAGE_KEY,
  DISTRICT_LOCAL_STORAGE_KEY,
  SELECTED_EOA_LOCAL_STORAGE_KEY,
  SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY,
  EOA_PREFERENCE_TTL_MS,
  SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY,
  AUDIT_LOG_ITEMS_PER_PAGE,
  DATA_UPLOAD_ITEMS_PER_PAGE,
  DATA_QC_TRANSCRIPTS_ITEMS_PER_PAGE,
  STUDENT_ROSTER_ITEMS_PER_PAGE,
  USERS_PER_PAGE,
  ACTIVITY_LOG_ITEMS_PER_PAGE,
  CHAT_CONVERSATIONS_PER_PAGE,
  TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS,
  USER_ROLES,
  USER_ROLES_LABELS,
  EOA_INVITE_ROLES,
  PLATFORM_INVITE_ROLES,
  UPLOAD_SECTIONS_IDS,
  NAVIGATION_LINKS_DATA,
  DEMOGRAPHIC_PERCENTAGES,
  fileTypesLabels,
  TRANSCRIPTS_CATEGORIES,
  TRANSCRIPTS_CATEGORIES_LABELS,
  TRANSCRIPTS_PAGE_SIZES,
  DATA_CATEGORY_LABELS,
  ACADEMIC_PERIODS_NAMES,
  ACADEMIC_PERIODS_LABELS,
  XQ_SUPPORT_EMAIL,
  XQ_INFO_EMAIL,
  DEMOGRAPHIC_SETTINGS_DATA_KEYS,
};
